import { activeSubscriptionSelector, entitlementUnknownSelector } from '@common-reducers/UserSelectors';
import { Box, CircularProgress, IconButton, Stack, Tooltip } from "@mui/material";

import { userMessagesSelectors } from "@/common/reducers/DBServiceThunks";
import { addScheduledMessageButtonActions } from "@common-reducers/AddScheduledMessageButtonReducer";
import { onDeleteRecurringDialogCloseThunk } from "@common-reducers/CalendarThunks";
import { useAppSelector, useAppDispatch } from "@common-reducers/hooks/store.hook";
import { useAnalyticsService } from "@common-reducers/hooks/use-analytics-service";
import { blobMap } from "@common-services/blob-service";
import { isExtensionContext } from '@common-services/detect-context';
import { handleMentionsFormatInMessage, openPaymentSite } from '@common-services/utils';
import { navigateToEngines } from '@/common/utils/navigation.util';
import { AnalyticsService } from "@common/services/analytics-service";
import { theme } from "@common/theme";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { usePortalContainer } from "@/common/hooks/use-portal-container";
import { sendScheduleMessageThunk, retrySendMessageThunk } from '@extension/context/content/thunks/WhatsappSendMessageThunk';
import clsx from 'clsx';
import moment from "moment";
import React, { ReactElement, useState, useMemo, useEffect } from "react";
import ContactAvatar from '../../extension/context/content/components/bulk-dashboard/contact-table/contact-avatar';
import { deleteUserMessage } from '../thunks/UserMessagesThunks';
import AttachmentPreview from "./attachment-preview/attachment-preview";
import RowStack from './common/row-stack/RowStack';
import WhatsAppPollPreview from './message-editor/whatsapp-poll-preview';
import ReplyToMessageChip from './message-editor/reply-to-message-chip';
import MessageProblem from "./message-problem";
import { isMessageOverdue } from "@/bt-common/services/utilsCommon";
import { Badge } from "@/components/ui/badge";
import { Clock, Loader2, SendHorizontal, AlertCircle, X, Pencil, RefreshCw, FileText, TriangleAlert, WifiOff, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { MessageStatus } from "@bt-common/schemas/bt/user-message";
import { wireStateFromStatus } from "@bt-common/services/message-status";
import { MessageLogCard } from "@/common/views/user-overview/tabs/message-logs/components/MessageLogCard";
import { DBMessageLogThunks, messageLogSelectByQuery } from "@common-reducers/DBServiceThunks";
import { TooltipContainerProvider } from "@/common/contexts/TooltipContainerContext";
import LexicalMessageEditor from "./message-editor/lexical/LexicalMessageEditor";
import { Temporal } from '@js-temporal/polyfill';
import { useTranslation } from 'react-i18next';
import {
  MESSAGE_STATUS_CONFIG,
  getEffectiveMessageStatus,
  EffectiveMessageStatus,
  canManuallySend,
  getUnconfirmedTooltipInfo,
  describeSendFailure
} from "@/common/components/message-status/message-status-config";
import { planRowDelete } from "@/common/components/message-status/row-actions";

type WhatsappMessageProps = {
  hideActions?: boolean;
  messageId: string;
  isDisplayContactAvatar?: boolean;
  onClose?: () => void;
  onDelete?: () => void;
  showSendNowButton?: boolean;
  shadowRoot?: ShadowRoot; // Shadow root for portal container (if in shadow DOM)
};

// MESSAGE_STATUS_CONFIG is now imported from message-status-config.ts

const singleLineEllipsis = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 1,
  WebkitBoxOrient: 'vertical',
}

export function WhatsappMessage({ showSendNowButton = true, messageId, isDisplayContactAvatar, shadowRoot }: WhatsappMessageProps): ReactElement {
  const { t, i18n } = useTranslation();
  const analyticsService: AnalyticsService = useAnalyticsService();
  const dispatch = useAppDispatch();
  const isExtension = isExtensionContext();
  const accessToken = useAppSelector((state) => state.UserReducer.accessToken);
  
  // Get portal container for TooltipContainerProvider (DialogContent gets it automatically from context)
  const tooltipContainer = usePortalContainer();

  // Use selector to get the message from the store
  const scheduledMessage = useAppSelector((state) => userMessagesSelectors.selectById(state, messageId));
  const activeSubscription = useAppSelector(activeSubscriptionSelector);
  const hasOfflineSocket = useMemo(() => activeSubscription?.hasSocket() ?? false, [activeSubscription]);
  // FE#159 — `?? false` above cannot tell "no offline sending" from "subscription not
  // loaded yet". Rendering from that guess is what showed a Pro customer the
  // "upgrade to work offline" upsell. While entitlement is unknown we render NEITHER
  // variant of the overdue notice; it appears the moment the subscription resolves.
  const entitlementUnknown = useAppSelector(entitlementUnknownSelector);
  const isOverdue = isMessageOverdue(scheduledMessage);

  // A message that already reached the wire (sent / sent_pending_ack / confirmed /
  // delivered / read) can never be "missed" — its due time is simply in the past.
  // Before this check the red "Missed X ago" notice was derived from dueDate ALONE,
  // so every unconfirmed row past its due time read as a send failure even though the
  // message had gone out. failed/expired rows keep the notice: for those the send
  // genuinely did not happen.
  const hasReachedWire = wireStateFromStatus(scheduledMessage?.status ?? 'pending') === 'sent';

  const relativeDueDate = useMemo(() => {
    try {
      if (!scheduledMessage?.dueDate) return "";
      const now = Temporal.Now.instant();
      const due = Temporal.Instant.from(new Date(scheduledMessage.dueDate).toISOString());
      const isPast = Temporal.Instant.compare(now, due) > 0;
      // Calculate duration
      const duration = isPast ? now.since(due) : due.since(now);
      
      // Round and format using toLocaleString
      // We specify largestUnit: 'day' to get days, hours, etc. (24h days for Instant)
      // smallestUnit: 'minute' to hide seconds
      const rounded = duration.round({ largestUnit: 'day', smallestUnit: 'minute' });
      const timeString = rounded.toLocaleString(i18n.language, { style: 'long' });

      // Under a minute the rounded duration is all-zero, and Intl.DurationFormat omits
      // zero-valued fields — so `timeString` comes back EMPTY and the sentence renders as
      // a bare " ago". That is precisely the window a just-sent row is read in, so it
      // needs its own phrasing rather than an interpolated blank.
      if (rounded.sign === 0 || !timeString) {
        return <span className="ml-1"> {t(isPast ? 'whatsappMessage.justNow' : 'whatsappMessage.inAMoment')}</span>;
      }

      if (isPast) {
         return hasReachedWire
           ? <span className="ml-1"> {t('whatsappMessage.timeAgo', { time: timeString })}</span>
           : <span className="text-red-500 font-medium ml-1"> {t('whatsappMessage.missedAgo', { time: timeString })}</span>;
      }
      return <span className="ml-1"> {t('whatsappMessage.inDuration', { time: timeString })}</span>;
    } catch (e) { return ""; }
  }, [scheduledMessage?.dueDate, i18n.language, hasReachedWire, t]);

  // const [pending, setPending] = useState(false);
  // const [isOverdue, setIsOverdue] = useState(false);
  const [isSending, setIsSending] = useState(false);
  // const [isSentSuccessfully, setIsSentSuccessfully] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showMessageLogDialog, setShowMessageLogDialog] = useState(false);
  const [isLoadingMessageLogs, setIsLoadingMessageLogs] = useState(false);


  // Function to update UI state
  // const updateUIState = useCallback(() => {
  //   const messageOverdue = isMessageOverdue(scheduledMessage);
  //   setIsOverdue(messageOverdue);

  //   const hasFailedToSend = checkIfFailedToSend(scheduledMessage.updatedAt);
  //   setFailedToSend(scheduledMessage.status === 'sent' && hasFailedToSend);
  //   setPending(scheduledMessage.status === 'sent' && !hasFailedToSend);
  //   setIsSentSuccessfully(scheduledMessage.status === 'confirmed' && !hasFailedToSend && !messageOverdue);
  // }, [scheduledMessage.dueDate, scheduledMessage.status, scheduledMessage.updatedAt]);



  // Function to check if the message has failed to send
  // const checkIfFailedToSend = (updatedAt) => {
  //   const lastUpdate = moment(updatedAt);
  //   const now = moment();
  //   return now.diff(lastUpdate, 'minutes') > 5;
  // };

  // Update UI state based on prop changes
  // useEffect(() => {
  //   updateUIState();
  // }, [scheduledMessage.status, scheduledMessage.updatedAt, scheduledMessage.dueDate, updateUIState]);

  // Interval to update UI when message status changes
  // useEffect(() => {
  //   if (scheduledMessage.status === 'sent') {
  //     const interval = setInterval(() => {
  //       updateUIState();
  //     }, 5000); // Adjust interval time as needed

  //     return () => clearInterval(interval);
  //   }
  // }, [scheduledMessage.status, scheduledMessage.updatedAt, scheduledMessage.dueDate, updateUIState]);

  const linkPreviewImage = scheduledMessage.linkPreview?.thumbnail;

  // Compute effective status: detects "expired" for sent messages past retry limit
  const effectiveStatus = getEffectiveMessageStatus(scheduledMessage);
  const isExpired = effectiveStatus === 'expired';
  const isFailed = effectiveStatus === 'failed';
  // In-flight ("unconfirmed" — sent_pending_ack or legacy sent): already handed to
  // WhatsApp, no delivery confirmation received. The Send/retry button's gate on this
  // state now lives in `canManuallySend` (message-status-config.ts) — see its call
  // below instead of a local isInFlight flag.

  // #166 rev2: the tooltip branches on the 48h ack-recoverable horizon (younger:
  // "waiting for confirmation" / older: "N days ago, may still have been delivered"),
  // using `lastSendAttempt` — the field stamped per actual send attempt — NOT
  // `dueDate` (rendered a few lines below as scheduledTime/scheduledDate). `dueDate`
  // rolls forward on repeating messages (the userMessage doc is reused across
  // recurring occurrences), so a message that is STILL `unconfirmed` can already show
  // a `dueDate` from a later occurrence than the one that was actually sent — see
  // MEMORY: doc reused across occurrences. `lastSendAttempt` exists on the schema
  // (user-message.ts) with total prod coverage on both source statuses, so
  // getUnconfirmedTooltipInfo reads it directly off scheduledMessage; absence (should
  // not happen in practice, but the schema marks it optional) falls back to the single
  // age-agnostic string.
  const unconfirmedTooltipInfo = getUnconfirmedTooltipInfo(scheduledMessage);
  const unconfirmedTooltip = t(unconfirmedTooltipInfo.key, unconfirmedTooltipInfo.params);

  // Derived from Redux state - status badge rendering (pure presentation)
  const renderStatusBadge = (status: EffectiveMessageStatus) => {
    const config = MESSAGE_STATUS_CONFIG[status];
    if (!config) return null;

    const IconComponent = config.icon;
    const tooltipText = status === 'unconfirmed' ? unconfirmedTooltip : t(config.description);

    return (
      <Tooltip title={tooltipText} arrow>
        <Badge variant="outline" className={cn(config.badgeClassName, "gap-1")}>
          <IconComponent className={cn("h-3 w-3", config.iconClassName)} />
          <span>{t(config.label)}</span>
        </Badge>
      </Tooltip>
    );
  };

  // Derived from Redux state - determine if message can be edited
  const canEdit = scheduledMessage.status === 'pending';

  // Derived from Redux state - contextual tooltip for Send Now button
  const sendButtonTooltip = scheduledMessage.status === 'pending'
    ? t('whatsappMessage.sendNow')
    : isExpired
    ? t('whatsappMessage.retryWindowExpiredTooltip')
    : t('whatsappMessage.retrySendTooltip');

  // Get message log row for this message (only when not pending)
  const userId = scheduledMessage.owner as string;
  
  // Get logs directly from Redux state for this specific messageId
  const messageLogs = useAppSelector((state) =>
    messageLogSelectByQuery(state.DBMessageLogReducer, {
      messageId: scheduledMessage._id
    })
  );

  // FE#249 — a confirming delivery log (delivered/read/confirmed) is proof the
  // message reached the recipient and supersedes a STALE doc-level `failed` /
  // `expired` status. In the dual-engine race the winning engine's confirm lands
  // in the logs while this same doc still reads failed/expired, so gate the red
  // failure/expired panels (and the stale `sendFailureError` text they render) on
  // the absence of any confirming log.
  const hasConfirmingDeliveryLog = (messageLogs ?? []).some(
    (log) => log.status === 'confirmed' || log.status === 'delivered' || log.status === 'read'
  );

  // Fetch message logs when dialog opens
  useEffect(() => {
    if (showMessageLogDialog && scheduledMessage._id) {
      setIsLoadingMessageLogs(true);
      dispatch(DBMessageLogThunks.find({
        messageId: scheduledMessage._id,
        $sort: {
          createdAt: -1
        },
        $limit: -1
      }))
      .finally(() => {
        setIsLoadingMessageLogs(false);
      });
    } else if (!showMessageLogDialog) {
      // Reset loading state when dialog closes
      setIsLoadingMessageLogs(false);
    }
  }, [showMessageLogDialog, scheduledMessage._id, dispatch]);

  // Compute MessageLogRow from fetched logs (similar to selectAllMessageLogRows logic)
  const messageLogRow = useMemo(() => {
    if (!messageLogs || messageLogs.length === 0 || scheduledMessage.status === 'pending') {
      return null;
    }

    const logs = messageLogs;
    
    // Sort logs by createdAt descending to get latest
    const sortedLogs = [...logs].sort((a, b) => {
      const timeA = new Date(a.createdAt || 0).getTime();
      const timeB = new Date(b.createdAt || 0).getTime();
      return timeB - timeA;
    });
    const latestLog = sortedLogs[0];

    // Determine latest status
    const latestStatus = latestLog.status as any;

    // FE#249 — latest-outcome-wins. A message that was ultimately DELIVERED must
    // not show an error just because an EARLIER attempt failed: the dual-engine
    // race writes a lock-loss 'failed' log for the very message the winning engine
    // then confirms. A confirming log (delivered/read/confirmed) is proof of
    // delivery that supersedes any earlier failure, so only surface an error when
    // there is NO confirming log AND a real failure/error is present.
    const hasConfirmingLog = logs.some(log =>
      log.status === 'confirmed' || log.status === 'delivered' || log.status === 'read');
    const hasError = !hasConfirmingLog && logs.some(log => log.status === 'failed' || log.error);

    // Group logs by attemptType and then by time proximity (10 seconds) - similar to selectAllMessageLogRows
    const logsByAttemptType = logs.reduce((acc: Record<string, typeof logs>, log) => {
      const attemptType = log.metadata?.attemptType || 'initial';
      if (!acc[attemptType]) {
        acc[attemptType] = [];
      }
      acc[attemptType].push(log);
      return acc;
    }, {});

    // Group logs within each attemptType by time proximity (10 seconds)
    const groupedLogs: any[][] = [];
    Object.values(logsByAttemptType).forEach(attemptLogs => {
      // Sort by createdAt
      const sortedAttemptLogs = [...attemptLogs].sort((a, b) => {
        const timeA = new Date(a.createdAt || 0).getTime();
        const timeB = new Date(b.createdAt || 0).getTime();
        return timeA - timeB;
      });

      // Group by 10-second intervals
      let currentGroup: any[] = [];
      let lastTime = 0;
      const TEN_SECONDS = 10000;

      sortedAttemptLogs.forEach(log => {
        const logTime = new Date(log.createdAt || 0).getTime();
        if (currentGroup.length === 0 || (logTime - lastTime) <= TEN_SECONDS) {
          currentGroup.push(log);
        } else {
          groupedLogs.push(currentGroup);
          currentGroup = [log];
        }
        lastTime = logTime;
      });
      
      if (currentGroup.length > 0) {
        groupedLogs.push(currentGroup);
      }
    });

    const row: any = {
      id: scheduledMessage._id,
      messageId: scheduledMessage._id,
      message: scheduledMessage,
      latestStatus,
      hasError,
      logCount: logs.length,
      latestUpdate: latestLog.createdAt || scheduledMessage.updatedAt,
      isMainRow: true,
      dueDate: scheduledMessage.dueDate,
      logs: groupedLogs
    };

    return row;
  }, [messageLogs, scheduledMessage]);

  // Only show message log button when message has started sending (not pending)
  const showMessageLogButton = scheduledMessage.status !== 'pending';

  // const handleClose = () => {
  //   if (props.onClose) props.onClose()
  // }

  const editMessage = () => {
    dispatch(
      addScheduledMessageButtonActions.editMessageFromContext({
        message: { ...scheduledMessage },
        context: 'calendar-sidebar' // Use calendar-sidebar context to get "all messages" behavior
      })
    );
    analyticsService.event("edit_message", {
      category: "messages",
      action: "edit",
      label: scheduledMessage.isRecurring ? "recurring" : "regular",
    });

    // handleClose()
  };

  const sendMessage = async (event: React.MouseEvent) => {
    event.stopPropagation();
    console.log(`[retry send] Button clicked - messageId: ${scheduledMessage._id}, status: ${scheduledMessage.status}, effectiveStatus: ${effectiveStatus}, isExpired: ${isExpired}, isFailed: ${isFailed}`);
    setIsSending(true); // Local UI state only
    try {
      // Route to appropriate thunk based on Redux state (scheduledMessage.status)
      if (scheduledMessage.status === 'pending') {
        console.log(`[retry send] Status is 'pending' - calling sendMessageThunk with isSendMessageNow=true`);
        await dispatch(sendScheduleMessageThunk({
          userMessage: scheduledMessage,
          isSendMessageNow: true
        }));
        console.log(`[retry send] sendMessageThunk completed`);
      } else {
        // For sent/failed messages, use retry flow
        console.log(`[retry send] Status is '${scheduledMessage.status}' - calling retrySendMessageThunk with retryTrigger='manual'`);
        console.log(`[retry send] scheduledMessage details:`, JSON.stringify({
          _id: scheduledMessage._id,
          status: scheduledMessage.status,
          retryCount: scheduledMessage.retryCount,
          dueDate: scheduledMessage.dueDate,
          lastSendAttempt: scheduledMessage.lastSendAttempt,
          contactList: scheduledMessage.contactList?.map((c: { id?: string; name?: string }) => ({ id: c.id, name: c.name }))
        }));
        const result = await dispatch(retrySendMessageThunk({ userMessage: scheduledMessage, retryTrigger: 'manual' }));
        console.log(`[retry send] retrySendMessageThunk completed - result:`, result);
      }
    } catch (error) {
      console.error('[retry send] Error sending message:', error);
    } finally {
      console.log(`[retry send] Finished - setting isSending=false`);
      setIsSending(false); // Local UI state only
    }
  }

  const deleteMessage = (event: React.MouseEvent) => {
    event.stopPropagation();
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirmed = async () => {
    // Handle recurring messages with "all messages" scope automatically.
    // NOTE (#149): planRowDelete does not gate on message status — see
    // row-actions.ts for why this is deliberately unchanged here.
    const rowDeletePlan = planRowDelete(scheduledMessage);
    if (rowDeletePlan.kind === 'recurring-scope-prompt') {
      await dispatch(onDeleteRecurringDialogCloseThunk({
        value: "All messages" as any,
        event: {
          _id: scheduledMessage._id,
          dueDate: scheduledMessage.dueDate?.toString(),
          isCampaign: false,
          isRecurring: true
        }
      }));
    } else {
      dispatch(deleteUserMessage(scheduledMessage._id));
    }

    analyticsService.event("remove_message", {
      category: "messages",
      action: "remove",
      label: scheduledMessage.isRecurring ? "recurring" : "regular",
    });

    //  handleClose();
  };

  const handleDeleteConfirmation = async (confirmed?: string) => {
    if (confirmed !== undefined) {
      await handleDeleteConfirmed();
    }
    setShowDeleteConfirmation(false);
  };

  const imageBlob = blobMap.get(scheduledMessage?.imageId);


  const buildActionContainer = () => (
    <Box 
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 8px',
        marginBottom: '8px',
        gap: '8px'
      }}
    >
      {/* Left side buttons */}
      <Box 
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        {canEdit && (
          <Tooltip title={t('whatsappMessage.editMessage')} arrow>
            <IconButton 
              onClick={editMessage}
              sx={{ 
                padding: 0,
                fontSize: '22px',
                color: '#ffffff',
                border: '1px solid transparent',
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: '0.2s all',
                background: 'linear-gradient(-135deg, #6dd1f1, var(--tasks-button-hover-bg))',
                boxShadow: '0 2px 2.5px rgba(0, 0, 0, 0.13)',
                '& svg': {
                  color: '#ffffff',
                },
                '&:hover': {
                  background: 'linear-gradient(-135deg, rgba(109, 209, 241, 0.7), rgba(37, 154, 233, 0.7))',
                },
              }}
            >
              <Pencil size={15} style={{ margin: !isExtension ? '0px 0px 1px 1px' : undefined }} />
            </IconButton>
          </Tooltip>
        )}

        {showSendNowButton && canManuallySend(scheduledMessage) && (
          <Tooltip
            PopperProps={{ style: { textAlign: "start" } }}
            arrow
            title={sendButtonTooltip}
          >
            <IconButton
              onClick={sendMessage}
              disabled={isSending}
              sx={{
                padding: 0,
                fontSize: '22px',
                color: '#ffffff',
                border: '1px solid transparent',
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: '0.2s all',
                background: 'linear-gradient(-135deg, #6dd1f1, var(--tasks-button-hover-bg))',
                boxShadow: '0 2px 2.5px rgba(0, 0, 0, 0.13)',
                '& svg': {
                  color: '#ffffff',
                },
                '&:hover': {
                  background: 'linear-gradient(-135deg, rgba(109, 209, 241, 0.7), rgba(37, 154, 233, 0.7))',
                },
                '&.Mui-disabled': {
                  opacity: 0.6,
                },
              }}
            >
              {isSending ? (
                <CircularProgress
                  size={15}
                  thickness={5}
                  sx={{
                    color: 'inherit',
                    margin: !isExtension ? '0px 0px 1px 3px' : undefined
                  }}
                />
              ) : (
                <SendHorizontal size={15} style={{ margin: !isExtension ? '0px 0px 1px 3px' : undefined }} />
              )}
            </IconButton>
          </Tooltip>
        )}

        {scheduledMessage.isRecurring && (
          <Tooltip
            PopperProps={{ style: { textAlign: "start" } }}
            arrow
            title={t('whatsappMessage.recurringMessage')}
          >
            <Box
              sx={{
                fontSize: '22px',
                color: '#ffffff',
                border: '1px solid transparent',
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: '0.2s all',
                boxShadow: 'unset',
                background: '#a3a3a3',
                cursor: 'default',
                padding: 0,
                '& svg': {
                  color: '#ffffff',
                },
              }}
            >
              <RefreshCw size={18} />
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Right side buttons */}
      <Box 
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        {/* Status Badge - derived from scheduledMessage.status (Redux state) */}
        {effectiveStatus && renderStatusBadge(effectiveStatus)}

        {scheduledMessage.status === 'pending' && isMessageOverdue(scheduledMessage) && (
          <Tooltip
            PopperProps={{ style: { textAlign: "start" } }}
            arrow
            title={(() => {
             try {
              if (hasOfflineSocket) return t('whatsappMessage.connectionUnavailable');
              
              const now = Temporal.Now.instant();
              const due = Temporal.Instant.from(new Date(scheduledMessage.dueDate).toISOString());
              
              if (Temporal.Instant.compare(now, due) > 0) {
                 const duration = now.since(due);
                 const rounded = duration.round({ largestUnit: 'day', smallestUnit: 'minute' });
                 return t('whatsappMessage.overdueBy', { time: rounded.toLocaleString(i18n.language, { style: 'short' }) });
              }
              return t('whatsappMessage.messageOverdue');
             } catch (e) {
               console.error("Temporal calculation error", e);
               return t('whatsappMessage.messageOverdue');
             }
           })()}
          >
            <Box
              sx={{
                fontSize: '22px',
                color: '#ffffff',
                border: '1px solid transparent',
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: '0.2s all',
                boxShadow: 'unset',
                background: hasOfflineSocket ? 'linear-gradient(-135deg, #d97706, #b45309)' : 'linear-gradient(-135deg, #fbbf24, #f59e0b)',
                cursor: 'default',
                padding: 0,
                '& svg': {
                  color: '#ffffff',
                },
              }}
            >
              {hasOfflineSocket ? <WifiOff size={15} color="#ffffff" /> : <TriangleAlert size={15} color="#ffffff" />}
            </Box>
          </Tooltip>
        )}

        {showMessageLogButton && (
          <Tooltip title={t('whatsappMessage.viewLogs')} arrow>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setShowMessageLogDialog(true);
              }}
              sx={{ 
                padding: 0,
                fontSize: '22px',
                color: '#ffffff',
                border: '1px solid transparent',
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: '0.2s all',
                background: 'linear-gradient(-135deg, #6dd1f1, var(--tasks-button-hover-bg))',
                boxShadow: '0 2px 2.5px rgba(0, 0, 0, 0.13)',
                '& svg': {
                  color: '#ffffff',
                },
                '&:hover': {
                  background: 'linear-gradient(-135deg, rgba(109, 209, 241, 0.7), rgba(37, 154, 233, 0.7))',
                },
              }}
            >
              <FileText size={15} style={{ margin: !isExtension ? '0px 0px 1px 1px' : undefined }} />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title={t('whatsappMessage.deleteMessage')} arrow>
          <IconButton
            onClick={deleteMessage}
            sx={{ 
              padding: 0,
              fontSize: '22px',
              color: '#ffffff',
              border: '1px solid transparent',
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: '0.2s all',
              background: 'linear-gradient(-135deg, #6dd1f1, var(--tasks-button-hover-bg))',
              boxShadow: '0 2px 2.5px rgba(0, 0, 0, 0.13)',
              '& svg': {
                color: '#ffffff',
              },
              '&:hover': {
                background: 'linear-gradient(-135deg, rgba(109, 209, 241, 0.7), rgba(37, 154, 233, 0.7))',
              },
            }}
          >
            <X size={16} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )

  const linkPreviewBox = () => <Stack direction='row' sx={{
    overflow: 'hidden',
    borderRadius: '5px',
    background: 'rgba(255, 255, 255, 0.5);',
    direction: 'inherit',
    height: 90,
  }}>
    <Box sx={{
      width: '90px',
      height: '90px',
      flexShrink: 0
    }}>
      <img
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover'
        }}
        alt=''
        src={linkPreviewImage}
      />
    </Box>
    <Stack sx={{
      p: '5px'
    }}>
      <Box sx={{
        ...singleLineEllipsis,
        mb: '2px',
        color: 'inherit;'
      }}>{scheduledMessage.linkPreview?.title?.slice(0, 26)}</Box>
      <Box sx={{
        ...singleLineEllipsis,
        mb: '2px',
        color: 'inherit;'
      }}>{scheduledMessage.linkPreview?.description?.slice(0, 26)}</Box>
      <Box sx={{
        ...singleLineEllipsis,
        mb: '2px',
        color: 'inherit;'
      }}>{scheduledMessage.linkPreview?.url}</Box>
    </Stack>
  </Stack>


  // const buildMessage = () => {
  //   return (

  //   );
  // };

  // const buildPlaceholder = () => {
  //   return (
  //     <div className={classes.placeholderWrap}>
  //       <Skeleton variant="circular" width={25} height={25} />
  //       <Skeleton variant="text" height={16} width={100} />
  //       <Skeleton variant="text" height={16} width={60} />
  //       <div
  //         style={{
  //           display: "grid",
  //           gridTemplateColumns: "38% 60%",
  //           justifyContent: "space-between",
  //         }}
  //       >
  //         <Skeleton height={12} variant="text" />
  //         <Skeleton height={12} variant="text" />
  //       </div>
  //     </div>
  //   );
  // };


  // Early return if message doesn't exist
  if (!scheduledMessage) {
    return <div>Message not found</div>;
  }

  // Derived from Redux state - status configuration for styling (uses effective status).
  // pending is always present in the config; the `!` satisfies the Partial<Record> type on the fallback.
  const statusConfig = MESSAGE_STATUS_CONFIG[effectiveStatus || 'pending'] || MESSAGE_STATUS_CONFIG.pending!;

  return (
    <>
      <Stack
        dir="auto"
        className={clsx(
          // For pending+overdue messages, show amber border; for expired, show red border from config
          (scheduledMessage.status === 'pending' && isOverdue) ? "border-l-[#f59e0b]" : statusConfig.borderColor,
          "border-l-4"
        )}
        sx={{
          borderRadius: '7.5px',
          padding: '6px 9px 8px 7px',
          backgroundColor: 'var(--whatsapp-bubble-sent)',
          boxShadow: '0 2px 2.5px rgba(0, 0, 0, 0.13)',
          fontSize: '14.2px',
          color: 'var(--whatsapp-text)',
          minWidth: '250px',
          maxWidth: '300px',
        }}
      >

        {/* <div>
          <div style={{
            backgroundColor: 'red',
            width: '100%',
            height: '100px',
          }}>
            wwwwwwwwww
          </div>
        </div> */}

        <>
          {buildActionContainer()}

          {scheduledMessage?.isPoll &&
            <>
              <WhatsAppPollPreview poll={scheduledMessage.poll} />
            </>
          }

          {!scheduledMessage?.isPoll &&
            <>
              <ReplyToMessageChip height='54px' scheduledMessage={scheduledMessage} />

              {scheduledMessage.quotedMessage?.body &&
                !scheduledMessage.quotedMessage?.blob ? (
                <div
                  style={{
                    backgroundColor: theme.palette.primary.main,
                    padding: "5px 8px 7px 12px",
                    borderRadius: 5,
                    marginTop: theme.spacing(1),
                    marginBottom: theme.spacing(1),
                  }}
                >
                  {scheduledMessage.quotedMessage?.body}
                </div>
              ) : (
                <></>
              )}
              <Box sx={{ mb: 1 }}>
                {scheduledMessage.sendLinkPreview && linkPreviewBox()}
                {!scheduledMessage.sendLinkPreview && <AttachmentPreview
                  attachment={imageBlob}
                  attachmentUrl={scheduledMessage.asset?.url}
                  attachmentType={scheduledMessage.asset?.type}
                  attachmentName={scheduledMessage.asset?.name}
                  previewMaxHeight='150px'
                />}
              </Box>
              {isDisplayContactAvatar &&
                <ContactAvatar
                  contactId={scheduledMessage?.contactList[0]?.id}
                  size={30}
                  sx={{
                    justifyContent: 'flex-start',
                    m: '-7px 0px 8px 9px',
                  }}
                />
              }
              <Box
                sx={{
                  overflowWrap: 'break-word',
                  hyphens: 'auto',
                  padding: '0 10px',
                  color: 'var(--whatsapp-text)',
                  whiteSpace: 'pre-wrap',
                  '& .lexical-editor': {
                    padding: '0px !important',
                    minHeight: 'auto !important'
                  }
                }}
              >
                <LexicalMessageEditor
                  isPreview={true}
                  content={scheduledMessage.message}
                  onFilesSelected={() => {}}
                />
              </Box>
            </>
          }
          <Box
            dir="auto"
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              fontSize: '11px',
              marginTop: '10px',
              color: 'var(--date-badge-text)',
              '& > div:first-of-type': {
                flexBasis: '5%',
                padding: '1px 10px',
              },
              '& > div:last-of-type': {
                borderLeft: '1px solid var(--date-badge-border)',
                padding: '1px 10px',
              },
            }}
          >
            <Tooltip title={t('whatsappMessage.scheduledTime')} arrow>
              <div>
                {moment(scheduledMessage.dueDate).format("HH:mm ")}</div>
            </Tooltip>
            <Tooltip title={t('whatsappMessage.scheduledDate')} arrow>
              <div>{moment(scheduledMessage.dueDate).format("DD/MM/yyyy")} {relativeDueDate}</div>
            </Tooltip>
          </Box>

          {/* Failed status - message failed to send (FE#249: suppressed once a
              confirming delivery log proves the send actually reached WhatsApp) */}
          {isFailed && !hasConfirmingDeliveryLog && (
            <div className="flex flex-col gap-2 p-3 mt-2 bg-red-50 rounded-md border border-red-200">
              <div className="flex items-center gap-2">
                <AlertCircle size={18} className="text-red-500" />
                <span className="font-semibold text-sm text-gray-800">
                  {t('whatsappMessage.messageFailed')}
                </span>
              </div>
              <p className="text-xs text-gray-600 pl-6">
                {describeSendFailure(scheduledMessage, t)}
              </p>
              <div className="flex gap-2 pl-6 mt-1 justify-end">
                <Button
                  size="sm"
                  className="h-7 text-xs text-white border-transparent"
                  style={{ background: 'linear-gradient(-135deg, #6dd1f1, var(--tasks-button-hover-bg))' }}
                  onClick={sendMessage}
                  disabled={isSending}
                >
                  {isSending ? t('whatsappMessage.sending') : t('whatsappMessage.retrySend')}
                </Button>
              </div>
            </div>
          )}

          {/* Expired status - message was "sent" (In Transit) but retry window has passed
              (FE#249: suppressed once a confirming delivery log proves delivery) */}
          {isExpired && !hasConfirmingDeliveryLog && (
            <div className="flex flex-col gap-2 p-3 mt-2 bg-red-50 rounded-md border border-red-200">
              <div className="flex items-center gap-2">
                <XCircle size={18} className="text-red-500" />
                <span className="font-semibold text-sm text-gray-800">
                  {t('whatsappMessage.retryWindowExpired')}
                </span>
              </div>
              <p className="text-xs text-gray-600 pl-6">
                {t('whatsappMessage.retryWindowExpiredDescription')}
              </p>
              <div className="flex gap-2 pl-6 mt-1 justify-end">
                <Button
                  size="sm"
                  className="h-7 text-xs text-white border-transparent"
                  style={{ background: 'linear-gradient(-135deg, #6dd1f1, var(--tasks-button-hover-bg))' }}
                  onClick={sendMessage}
                  disabled={isSending}
                >
                  {isSending ? t('whatsappMessage.sending') : t('whatsappMessage.retrySend')}
                </Button>
              </div>
            </div>
          )}

          {scheduledMessage.status === 'pending' && isMessageOverdue(scheduledMessage) && !entitlementUnknown && (
             <div className="flex flex-col gap-2 p-3 mt-2 bg-amber-50 rounded-md border border-amber-200">
               <div className="flex items-center gap-2">
                 {hasOfflineSocket ? <WifiOff size={18} className="text-amber-700" /> : <TriangleAlert size={18} className="text-amber-600" />}
                 <span className="font-semibold text-sm text-gray-800">
                   {hasOfflineSocket ? t('whatsappMessage.cloudSendingUnavailable') : t('whatsappMessage.windowMissed')}
                 </span>
               </div>
               <p className="text-xs text-gray-600 pl-6">
                 {hasOfflineSocket 
                   ? t('whatsappMessage.cloudUnavailableDescription')
                   : t('whatsappMessage.offlineDescription')}
               </p>
               <div className="flex gap-2 pl-6 mt-1 justify-end">
                 {hasOfflineSocket ? (
                   <Button
                     variant="ghost"
                     size="sm"
                     className="h-7 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                     onClick={(e) => {
                        e.stopPropagation();
                        navigateToEngines();
                     }}
                   >
                     {t('whatsappMessage.manageConnection')}
                   </Button>
                 ) : (
                   <Button
                     variant="outline"
                     size="sm"
                     className="h-7 text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
                     onClick={(e) => {
                       e.stopPropagation();
                       openPaymentSite(accessToken ?? '');
                     }}
                   >
                     {t('whatsappMessage.upgradeToCloud')}
                   </Button>
                 )}
                 <Button 
                   size="sm" 
                   className="h-7 text-xs text-white border-transparent"
                   style={{ background: 'linear-gradient(-135deg, #6dd1f1, var(--tasks-button-hover-bg))' }}
                   onClick={sendMessage}
                   disabled={isSending}
                 >
                   {t('whatsappMessage.sendNow')}
                 </Button>
               </div>
             </div>
           )}
        </>
      </Stack>

      <Dialog open={showDeleteConfirmation} onOpenChange={(open) => !open && setShowDeleteConfirmation(false)}>
        <DialogContent className="max-w-md p-6 sm:p-8">
          <DialogHeader className="space-y-3">
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              {t('whatsappMessage.deleteConfirmationTitle')}
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground">
              {t('whatsappMessage.deleteConfirmationDescription')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-6 flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirmation(false)}
            >
              {t('whatsappMessage.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                handleDeleteConfirmed();
                setShowDeleteConfirmation(false);
              }}
            >
              {t('whatsappMessage.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Message Log Dialog */}
      <Dialog 
        open={showMessageLogDialog} 
        onOpenChange={(open) => setShowMessageLogDialog(open)}
      >
        <DialogContent
          className="max-w-4xl w-full h-[90vh] max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0"
          style={{
            backgroundColor: 'var(--color-surface)',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            height: '90vh',
            maxHeight: '90vh',
            direction: 'inherit'
          }}
        >
          <style>{`
            [data-radix-dialog-content] button[class*="absolute right-4 top-4"] {
              display: none !important;
            }
          `}</style>
          <TooltipContainerProvider container={tooltipContainer}>
            <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-solid border-[var(--color-border)] sticky top-0 bg-[var(--color-surface)] pr-12 relative" style={{ boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}>
              <DialogTitle>{t('whatsappMessage.messageLogsTitle')}</DialogTitle>
              <DialogDescription>
                {t('whatsappMessage.messageLogsDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto px-6 pt-4 pb-4 min-h-0" style={{ scrollPaddingTop: '0', marginTop: 0 }}>
              {isLoadingMessageLogs ? (
                <div className="flex items-center justify-center py-12">
                  <CircularProgress size={40} />
                </div>
              ) : messageLogRow ? (
                <MessageLogCard messageId={messageLogRow.messageId} />
              ) : (
                <div className="text-center py-8 text-slate-500">
                  {t('whatsappMessage.noLogsFound')}
                </div>
              )}
            </div>
          </TooltipContainerProvider>
        </DialogContent>
      </Dialog>
    </>
  );
}
